<script setup>

import {computed, nextTick, onMounted, ref, watch} from "vue";

const emit = defineEmits(['update:colors'])

const props = defineProps({
    primaryColor: {
        type: String,
        required: true,
    },
    primaryContrastColor: {
        type: String,
        required: true,
    },
    primaryBackgroundColor: {
        type: String,
        required: true,
    },
    primaryForegroundColor: {
        type: String,
        required: true,
    },
    secondaryBackgroundColor: {
        type: String,
        required: true,
    },
    secondaryForegroundColor: {
        type: String,
        required: true,
    },
    cardBackgroundColor: {
        type: String,
        required: true,
    },
    cardForegroundColor: {
        type: String,
        required: true,
    },
    cardBorderColor: {
        type: String,
        required: true,
    },
    pinstripeColor: {
        type: String,
        required: true,
    },
})

const _primaryColor = ref(props.primaryColor)
const _primaryContrastColor = ref(props.primaryContrastColor)

const _primaryBackgroundColor = ref(props.primaryBackgroundColor)
const _primaryForegroundColor = ref(props.primaryForegroundColor)

const _secondaryBackgroundColor = ref(props.secondaryBackgroundColor)
const _secondaryForegroundColor = ref(props.secondaryForegroundColor)

const _cardBackgroundColor = ref(props.cardBackgroundColor)
const _cardForegroundColor = ref(props.cardForegroundColor)
const _cardBorderColor = ref(props.cardBorderColor)

const _pinstripeColor = ref(props.pinstripeColor)

const colors = computed(() => {
    return {
        primaryColor: _primaryColor.value,
        primaryContrastColor: _primaryContrastColor.value,
        primaryBackgroundColor: _primaryBackgroundColor.value,
        primaryForegroundColor: _primaryForegroundColor.value,
        secondaryBackgroundColor: _secondaryBackgroundColor.value,
        secondaryForegroundColor: _secondaryForegroundColor.value,
        cardBackgroundColor: _cardBackgroundColor.value,
        cardForegroundColor: _cardForegroundColor.value,
        cardBorderColor: _cardBorderColor.value,
        pinstripeColor: _pinstripeColor.value,
    }
})

watch(colors, (newColors) => {
    emit('update:colors', newColors)
}, {deep: true})

onMounted(() => {
    setTimeout(function(){
        _primaryColor.value = props.primaryColor
        _primaryContrastColor.value = props.primaryContrastColor

        _primaryBackgroundColor.value = props.primaryBackgroundColor
        _primaryForegroundColor.value = props.primaryForegroundColor

        _secondaryBackgroundColor.value = props.secondaryBackgroundColor
        _secondaryForegroundColor.value = props.secondaryForegroundColor

        _cardBackgroundColor.value = props.cardBackgroundColor
        _cardForegroundColor.value = props.cardForegroundColor
        _cardBorderColor.value = props.cardBorderColor

        _pinstripeColor.value = props.pinstripeColor
    }, 800)
})

</script>

<template>
    <div>
        <div>
            <div class="space-y-6">
                <card-basic
                    title="Primary"
                    value="primary-color"
                    :active="true"
                >
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <input-label label="Primary background"></input-label>
                            <color-picker
                                :color="_primaryBackgroundColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_primaryBackgroundColor = $event"
                            ></color-picker>
                        </div>
                        <div>
                            <input-label label="Primary text"></input-label>
                            <color-picker
                                :color="_primaryForegroundColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_primaryForegroundColor = $event"
                            ></color-picker>
                        </div>
                    </div>
                </card-basic>
                <card-basic
                    title="Secondary"
                    value="secondary-color"
                >
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <input-label label="Secondary background"></input-label>
                            <color-picker
                                :color="_secondaryBackgroundColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_secondaryBackgroundColor = $event"
                            ></color-picker>
                        </div>
                        <div>
                            <input-label label="Secondary text"></input-label>
                            <color-picker
                                :color="_secondaryForegroundColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_secondaryForegroundColor = $event"
                            ></color-picker>
                        </div>
                    </div>
                </card-basic>

                <card-basic
                    title="Buttons"
                    value="button-color"
                    :active="true"
                >
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <input-label label="Primary foreground color"></input-label>
                            <p class="text-xs text-gray-500 italic">
                                Used for buttons, icons and interactive elements.
                            </p>
                            <color-picker
                                :color="_primaryColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_primaryColor = $event"
                            ></color-picker>
                        </div>
                        <div>
                            <input-label label="Primary foreground text color"></input-label>
                            <p class="text-xs text-gray-500 italic">
                                Used for button texts.
                            </p>
                            <color-picker
                                :color="_primaryContrastColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_primaryContrastColor = $event"
                            ></color-picker>
                        </div>
                    </div>
                </card-basic>

                <card-basic
                    title="Card"
                    value="card-color"
                >
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <input-label label="Card background"></input-label>
                            <color-picker
                                :color="_cardBackgroundColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_cardBackgroundColor = $event"
                            ></color-picker>
                        </div>
                        <div>
                            <input-label label="Card text"></input-label>
                            <color-picker
                                :color="_cardForegroundColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_cardForegroundColor = $event"
                            ></color-picker>
                        </div>
                    </div>
                </card-basic>

                <card-basic
                    title="Borders"
                    value="pinstripe-color"
                >
                    <div class="grid grid-cols-2 gap-4">
                        <div>
                            <input-label label="Card border color"></input-label>
                            <color-picker
                                :color="_cardBorderColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_cardBorderColor = $event"
                            ></color-picker>
                        </div>
                        <div>
                            <input-label label="Bottom bar border color"></input-label>
                            <color-picker
                                :color="_pinstripeColor"
                                :debounce="10"
                                :show-palette="true"
                                format="hex"
                                @colorChanged="_pinstripeColor = $event"
                            ></color-picker>
                        </div>
                    </div>
                </card-basic>


            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
